import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import siteMessage from "@insite/client-framework/SiteMessage";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormFileUploadLabelPositions,
    DynamicFormFileUploadLabelPositionsLeft,
    DynamicFormFileUploadLabelPositionsNone,
    DynamicFormFileUploadLabelPositionsTop,
    DynamicFormsFileUploadModel,
} from "@midland-industries/Types/DynamicFormModels";
import { determineFormFieldIndexes } from "@midland-industries/Utilities/DetermineFormFieldIndexes";
import { determineFormIndex } from "@midland-industries/Utilities/DetermineFormIndex";
import { DynamicFormContext } from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import React, { useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { AnyStyledComponent, css } from "styled-components";

/**
 * IMPORTANT NOTES:
 * When using setDynamicFormFieldValue, the DynamicFormFileUpload widget puts fileName in the label property.
 * For this widget type, the label property does nothing on the server-side.
 */

interface FileExtensionModel {
    fields: {
        fileExtension: string;
    };
}

const enum fields {
    key = "key",
    labelPosition = "labelPosition",
    label = "label",
    isRequired = "isRequired",
    allowedFileExtensions = "allowedFileExtensions",
}

interface OwnProps {
    maxFileSizeMB?: number;
    fields: {
        [fields.key]: string;
        [fields.labelPosition]: DynamicFormFileUploadLabelPositions;
        [fields.label]: string;
        [fields.isRequired]: boolean;
        [fields.allowedFileExtensions]: FileExtensionModel[];
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DynamicFormFileUploadStyles {
    wrapper?: InjectableCss;
    uploadWrapper?: InjectableCss;
    fileUploader?: FileUploadPresentationProps;
    acceptedFileTypesWrapper?: InjectableCss;
    acceptedFileTypes?: TypographyPresentationProps;
    maximumFileSizeWrapper?: InjectableCss;
    maximumFileSize?: TypographyPresentationProps;
}

export const dynamicFormFileUploadStyles: DynamicFormFileUploadStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-top: 0.5em;
            margin-bottom: 1em;
        `,
    },
    acceptedFileTypesWrapper: {
        css: css`
            margin: 5px 10px;
        `,
    },
    maximumFileSizeWrapper: {
        css: css`
            margin: 5px 10px;
        `,
    },
};

const styles = dynamicFormFileUploadStyles;

const DynamicFormFileUpload: React.FC<Props> = ({
    maxFileSizeMB,
    fields,
    dynamicFormData,
    setDynamicFormFieldValue,
}) => {
    const { key, label, labelPosition, isRequired, allowedFileExtensions } = fields;

    const dynamicFormContext = useContext(DynamicFormContext);
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);

    const [file, setFile] = useState<DynamicFormsFileUploadModel | undefined>(undefined);
    const [fileError, setFileError] = useState<string>("");
    const [acceptedFileTypesArray, setAcceptedFileTypesArray] = useState<string[]>([]);
    const [acceptedFileTypes, setAcceptedFileTypes] = useState<string>("");
    const [fieldIndex, setFieldIndex] = useState(-1);
    const [formIndex, setFormIndex] = useState(-1);

    useEffect(() => {
        initializeFileUploadWidget();
        configureAcceptedFileTypes();
    }, [dynamicFormData?.forms]);

    useEffect(() => {
        const formName = dynamicFormContext.formName;
        const { isValid, errorMessage } = validateUploadedFile();

        const fileName = file?.fileName || "";

        setDynamicFormFieldValue({
            formName,
            formField: {
                key,
                group: "",
                label: fileName,
                isRequired,
                value: file?.bytes,
                type: "DynamicFormFileUpload",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    }, [file]);

    useEffect(() => {
        if (file?.bytes !== dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value) {
            if (dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value === undefined) {
                setFile(undefined);
            }
        }
    }, [dynamicFormData?.forms[formIndex]?.fields[fieldIndex]]);

    const configureAcceptedFileTypes = () => {
        const formattedFileExtensionsArray: string[] = [];
        let formattedFileExtensions = "";

        allowedFileExtensions.forEach((fileExtension: FileExtensionModel) => {
            let tempFileExtension = fileExtension.fields.fileExtension;
            if (tempFileExtension) {
                tempFileExtension = tempFileExtension.replace(".", "");
                tempFileExtension = tempFileExtension.toLowerCase().trim();
                if (tempFileExtension.length > 0) {
                    formattedFileExtensionsArray.push(tempFileExtension);
                    formattedFileExtensions +=
                        formattedFileExtensions.length > 0 ? `, .${tempFileExtension}` : `.${tempFileExtension}`;
                }
            }
        });

        setAcceptedFileTypesArray(formattedFileExtensionsArray);
        setAcceptedFileTypes(formattedFileExtensions);
    };

    const initializeFileUploadWidget = () => {
        const formFieldIndexes = determineFormFieldIndexes(dynamicFormContext.formName, key, dynamicFormData);
        setFormIndex(formFieldIndexes.formIndex);
        setFieldIndex(formFieldIndexes.fieldIndex);

        // No form registered in state
        if (formFieldIndexes.formIndex < 0) {
            return;
        }

        // Widget already registered in state
        if (formFieldIndexes.formIndex > -1 && formFieldIndexes.fieldIndex > -1) {
            return;
        }

        const { isValid, errorMessage } = validateUploadedFile();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label: file?.fileName || "",
                isRequired,
                value: file?.bytes,
                type: "DynamicFormFileUpload",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    };

    /**
     * Main validation function for DynamicFormFileUpload widget.
     */
    const validateUploadedFile = () => {
        const uploadedFile = file;

        // Handle scenario where there is no file
        if (!uploadedFile) {
            if (isRequired) {
                // Return a validation error if the file is required
                setFileError(siteMessage("DynamicForms_FileUpload_Required") as string);
                return {
                    isValid: false,
                    errorMessage: siteMessage("DynamicForms_FileUpload_Required") as string,
                };
            } 
                // Return as valid if the file is not required
                return {
                    isValid: true,
                    errorMessage: "",
                };
            
        }

        // Handle scenario where the file is empty (0 bytes)
        if (isRequired && !uploadedFile?.bytes) {
            setFileError(siteMessage("DynamicForms_FileUpload_Empty") as string);
            return {
                isValid: false,
                errorMessage: siteMessage("DynamicForms_FileUpload_Empty") as string,
            };
        }

        // Handle scenario where file too large
        if (maxFileSizeMB) {
            const maxB = maxFileSizeMB * 1024 * 1024;
            if (maxB < Number(uploadedFile.bytes)) {
                setFileError(siteMessage("DynamicForms_FileUpload_TooLarge", maxFileSizeMB.toString()) as string);
                return {
                    isValid: false,
                    errorMessage: siteMessage("DynamicForms_FileUpload_TooLarge", maxFileSizeMB.toString()) as string,
                };
            }
        }

        // Handle scenario where file type is not valid
        const fileExtension = getFileExtension(uploadedFile?.fileName || "");
        const matchingFileExtensionIndex = acceptedFileTypesArray.indexOf(fileExtension);
        if (matchingFileExtensionIndex === -1) {
            setFileError(siteMessage("DynamicForms_FileUpload_FileType") as string);
            return {
                isValid: false,
                errorMessage: siteMessage("DynamicForms_FileUpload_FileType") as string,
            };
        }

        setFileError("");
        return {
            isValid: true,
            errorMessage: "",
        };
    };

    const fileChangeHandler = (event: React.ChangeEvent<AnyStyledComponent>) => {
        const uploadedFile = event.target.files && event.target.files[0];
        const newFileUpload = new DynamicFormsFileUploadModel(key);
        if (uploadedFile) {
            newFileUpload.fileName = uploadedFile.name;
            newFileUpload.file = uploadedFile;
            getBase64(uploadedFile).then(data => {
                newFileUpload.bytes = String(data).split(",")[1];
                setFile(newFileUpload);
            });
        } else {
            setFile(newFileUpload);
        }
    };

    function getBase64(file: any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    const getDisplayValidationErrors = () => {
        const formIndex = determineFormIndex(dynamicFormContext.formName, dynamicFormData).formIndex;
        if (formIndex < 0) {
            return false;
        }

        return dynamicFormData?.forms[formIndex]?.displayValidationErrors;
    };

    type FinalLabelPosition = "left" | "top" | undefined;
    const FinalLabelPositionLeft: FinalLabelPosition = "left";
    const FinalLabelPositionTop: FinalLabelPosition = "top";
    const FinalLabelPositionUndefined: FinalLabelPosition = undefined;

    const determineFinalLabelPosition = () => {
        let returnValue: FinalLabelPosition = FinalLabelPositionTop;
        switch (labelPosition) {
            case DynamicFormFileUploadLabelPositionsLeft:
                returnValue = FinalLabelPositionLeft;
                break;
            case DynamicFormFileUploadLabelPositionsTop:
                returnValue = FinalLabelPositionTop;
                break;
            case DynamicFormFileUploadLabelPositionsNone:
                returnValue = FinalLabelPositionUndefined;
                break;
            default:
                returnValue = FinalLabelPositionTop;
                break;
        }
        return returnValue;
    };

    const finalLabelPosition: FinalLabelPosition = determineFinalLabelPosition();

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.uploadWrapper}>
                <FileUpload
                    {...styles.fileUploader}
                    inputRef={fileUploadRef}
                    fileName={file?.fileName || ""}
                    accept={acceptedFileTypes}
                    label={labelPosition === DynamicFormFileUploadLabelPositionsNone ? undefined : label}
                    labelPosition={finalLabelPosition}
                    error={getDisplayValidationErrors() && fileError}
                    onFileChange={fileChangeHandler}
                    uid={key}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.acceptedFileTypesWrapper}>
                <Typography {...styles.acceptedFileTypes}>{`Supported File Types: ${acceptedFileTypes}`}</Typography>
            </StyledWrapper>
            {maxFileSizeMB && (
                <StyledWrapper {...styles.maximumFileSizeWrapper}>
                    <Typography {...styles.maximumFileSize}>{`Maximum File Size: ${maxFileSizeMB}MB`}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicFormFileUpload);
